import PropTypes from 'prop-types'
import React from 'react'
import './styles.css'
import Collapse, { Panel } from 'rc-collapse'
import Tooltip from '../Tooltip/Tooltip'
import Icon from '../Icon/Icon'
import { v4 as uuid } from 'uuid'

const color = PropTypes.color
const WEIGHT = ['normal', 'bold', 'bolder']
const BORDER_STYLES = ['dotted', 'dashed', 'solid', 'none']
const WHITE_SPACE = ['normal', 'nowrap', 'pre', 'pre-wrap', 'pre-line', 'break-spaces']
const WORD_BREAK = ['normal', 'break-all', 'keep-all', 'break-word']
export const ICON_SIZE = ['lg', '2x', '3x', '4x', '5x']

const tooltipTheme = {
  color: color,
  backgroundColor: color,
  borderWidth: PropTypes.string,
  borderColor: color,
  borderStyle: PropTypes.oneOf(BORDER_STYLES),
  borderRadius: PropTypes.string,
  margin: PropTypes.string,
  padding: PropTypes.string,
  boxShadow: PropTypes.string,
  maxWidth: PropTypes.string,
  whiteSpace: PropTypes.oneOf(WHITE_SPACE),
  wordBreak: PropTypes.oneOf(WORD_BREAK)
}

const iconTheme = {
  iconColor: color,
  iconSize: PropTypes.oneOf(ICON_SIZE),
  iconCustomSize: PropTypes.string,
  iconRotation: PropTypes.bool
}

export const theme = {
  headerBackground: color,
  headerColor: color,
  headerFontSize: PropTypes.string,
  headerFontWeight: PropTypes.oneOf(WEIGHT),
  contentBackground: color,
  tooltipStyles: PropTypes.shape(tooltipTheme),
  leftIconStyles: PropTypes.shape(iconTheme),
  rightIconStyles: PropTypes.shape(iconTheme),
}

const lastOpened = new Map();

export default class Accordion extends React.Component {
  static propTypes = {
    /**
     * Currently opened accordion panel; if none provided an internal state will handle it
     */
    activeKey: PropTypes.number,
    /**
     * Default opened accordion panel; starts from 1. If a string is used, it's assumed to be the panel label
     */
    defaultActiveKey: PropTypes.string,
    /**
     * If accordion is true, only one panel can be open at a time. If accordion is set to false, multiple panels can be opened
     */
    accordion: PropTypes.bool,
    /**
     * Show/hide arrow beside header text
     */
    showArrow: PropTypes.bool,
    /**
     * Adjusts the colors, borders and alignment, etc.
     */
    styling: PropTypes.shape(theme),
    /**
     * Accordions in the same category share last opened section state
     */
    category: PropTypes.string,
  }

  static defaultProps = {
    defaultActiveKey: 1,
    accordion: true,
    showArrow: true,
    styling: {
      headerBackground: '#fff',
      headerColor: 'inherit',
      headerFontSize: '1em',
      headerFontWeight: 'normal',
      contentBackground: '#fff'
    }
  }


  constructor (props) {
    super(props)

    const { defaultActiveKey, category } = props

    const lastOpenedName = category && lastOpened.get(category)
    let activeKey = this._getActiveKeyByNameOrNumber(defaultActiveKey || lastOpenedName)
    this.state = { 
      activeKey,
      changeTab: (nameOrIndex) => this.onChange(this._getActiveKeyByNameOrNumber(nameOrIndex))
    }
    this.onChange = this.onChange.bind(this)
  }

  onChange (activeKey) {
    this.setState({ activeKey })
    if (this.props.category && this.getCardsData()[activeKey]) {
      lastOpened.set(this.props.category, this.getCardsData()[activeKey].props?.component?.name)
    }
  };

  _getActiveKeyByNameOrNumber (v) {
    const searchTitle = isNaN(parseInt(v)) ? v : ''
    let activeKey;
    const cards = this.getCardsData();
    if (searchTitle) {
      let activeKeyIndex = cards?.findIndex((c) => c.props?.component?.name === searchTitle)
      if (activeKeyIndex === -1) {
        activeKeyIndex = 0
      }
      activeKey = cards?.[activeKeyIndex]?.props?.component?.id
    } else if (!isNaN(parseInt(v))){
      //tab's numeric indexes are 1-based
      let activeKeyIndex = parseInt(v) >= 1 ? parseInt(v) : 1
      activeKey = cards?.[activeKeyIndex - 1]?.props?.component?.id
    }
    return activeKey
  }

  getCardsLayout () {
    const { showArrow } = this.props
    let cards = this.getCardsData()
    const items = []
    cards && Array.isArray(cards) && cards.map((card, index) => {
      if (!card) return
      const { component } = card.props || {}

      const { name, icon, extraLabel, extraIcon, extraIconColor, extraTooltip } = component?.mapping?.asObject || {}
      const cardName = name?.value || component?.name || 'Card'

      items.push(
        <Panel
          showArrow={showArrow}
          header={icon ? this.getIconHeader({isPrimary: true, icon: icon?.value, label: cardName}) : cardName}
          extra={extraIcon ? this.getIconHeader({isPrimary: false, icon: extraIcon?.value, extraIconColor: extraIconColor?.value, label: extraLabel?.value, tooltip: extraTooltip?.value}) : extraLabel?.value}
          key={component?.id}
          {...card.props}
        >
          {card}
        </Panel>
      )
    })

    return items
  }

  getIconHeader = ({isPrimary, icon, label, tooltip = '', extraIconColor}) => {
    const { tooltipStyles, leftIconStyles, rightIconStyles } = this.props.styling
    const iconStyling = isPrimary ? leftIconStyles : rightIconStyles
    const iconSize = isPrimary ? leftIconStyles?.iconSize : rightIconStyles?.iconSize
    const iconRotation = isPrimary ? leftIconStyles?.iconRotation : rightIconStyles?.iconRotation
    return (
      <div>
        {
          tooltip
            ? <Tooltip
              tooltipText={tooltip}
              {...tooltipStyles && {styling: { tooltipStyles }}}
            >
              <Icon
                styling={{ ...iconStyling, ...((extraIconColor && !isPrimary) && { iconColor: extraIconColor }) }}
                icon={icon}
                iconSize={iconSize}
                iconRotation={iconRotation}
              />
              <span>{label}</span>
            </Tooltip>
            : <>
              <Icon
                styling={{ ...iconStyling, ...((extraIconColor && !isPrimary) && { iconColor: extraIconColor }) }}
                icon={icon}
                iconSize={iconSize}
                iconRotation={iconRotation}
              />
              <span>{label}</span>
            </>
        }
      </div>
    )
  }

  getCardsData () {
    let cards = []
    if (this.props.children && Array.isArray(this.props.children) && this.props.children.length > 0) {
      cards = this.props.children
    }
    return cards
  }

  render () {
    const { activeKey } = this.state
    const {
      accordion,
      styling: {
        headerBackground,
        headerColor,
        headerFontSize,
        headerFontWeight,
        contentBackground
      } = {}
    } = this.props
    return (
      <div>
        <Collapse
          accordion={accordion}
          onChange={this.onChange}
          activeKey={(this.props.activeKey && this.props.activeKey.toString()) || this.state.activeKey}
        >
          {this.getCardsLayout()}
        </Collapse>
        <style jsx>{`
          div :global(.rc-collapse) {
              background-color: #FFFFFF;
              border-radius: 8px;
              overflow: hidden;
          }

          div :global(.rc-collapse > .rc-collapse-item > .rc-collapse-header) {
              display: flex;
              align-items: center;
              height: 45px;
              background: ${headerBackground};
              color: ${headerColor};
              font-size: ${headerFontSize};
              font-weight: ${headerFontWeight};
          }

          div :global(.rc-collapse > .rc-collapse-item > .rc-collapse-header:focus) {
              outline: 0;
          }

          div :global(.rc-collapse-content) {
              border-top: 1px solid #d9d9d9;
              background: ${contentBackground};
          }
        `}</style>
      </div>)
  }
}
